import { gql } from 'graphql-request'

// Query Keys
export const CLIENT_SERVICES_QUERY_KEY = 'CLIENT_SERVICES_QUERY'
export const SERVICE_CREDIT_USAGE_HISTORY_QUERY_KEY = 'SERVICE_CREDIT_USAGE_HISTORY_QUERY'
export const SERVICE_CREDIT_SUMMARY_PERIODS_QUERY_KEY = 'SERVICE_CREDIT_SUMMARY_PERIODS_QUERY'
export const SERVICE_CREDIT_ADJUSTMENT_NOTES_QUERY_KEY = 'SERVICE_CREDIT_ADJUSTMENT_NOTES_QUERY'
export const AVAILABLE_PLAN_OPTIONS_QUERY_KEY = 'AVAILABLE_PLAN_OPTIONS_QUERY'
export const CALCULATE_CART_QUERY_KEY = 'CALCULATE_CART_QUERY'
export const PREVIEW_PLAN_CHANGE_QUERY_KEY = 'PREVIEW_PLAN_CHANGE_QUERY'
export const SERVICE_TEMPLATE_AGREEMENT_QUERY_KEY = 'SERVICE_TEMPLATE_AGREEMENT_QUERY'

// Queries
export const clientServicesQuery = gql`
  query ClientServices(
    $clientId: String!
    $siteId: String
    $search: String
    $status: [ServiceStatus!]
    $type: [String!]
    $cursor: String
    $pageSize: Float
    $sortBy: ClientServicesSortBy
    $sortOrder: SortOrder
    $sharedSiteIds: [String!]
  ) {
    clientServices(
      clientId: $clientId
      siteId: $siteId
      search: $search
      status: $status
      type: $type
      cursor: $cursor
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
      sharedSiteIds: $sharedSiteIds
    ) {
      pageInfo {
        hasNextPage
        totalRecords
        startCursor
        endCursor
      }
      results {
        cursor
        row {
          serviceId
          serviceTemplateId
          clientId
          siteId
          status
          purchasedAt
          startsAt
          activatedStartDate
          expiresAt
          autoRenew
          nextBillingDate
          billingCycleAmount
          billingCycleUnit
          canceledAt
          cancellationReason
          onHold
          creditBalanceSnapshot {
            totalBalance
            hasUnlimited
            lastUpdated
            allocations {
              allocationId
              allocated
              balance
              consumed
              creditType
              expiresAt
              sessionTypeId
              sessionTypeIds
              sessionTypeName
              activateOnFirstUse
              expirationAmount
              expirationUnit
              validFrom
              validTo
            }
          }
          createdAt
          serviceTemplateName
          serviceTemplateType
          price
          currency
          isRefunded
          createdByInvoiceId
          referredByStaffName
          nextInvoiceDate
          recurringInvoices {
            recurringInvoiceId
            name
            amount
          }
          scheduledCancellation {
            serviceCancellationId
            status
            cancellationDate
            cancellationReason
          }
          activeSuspension {
            endDate
          }
          scheduledPlanChange {
            servicePlanChangeId
            status
            effectiveDate
            toServiceTemplateId
            toServiceTemplateName
          }
          lastExecutedPlanChange {
            servicePlanChangeId
            fromServiceTemplateName
            executedAt
            initiatedByStaffName
          }
          changePlanEnabled
          minimumHomeUsageInDays
          sharedHomeUsageUnlockDate
          isAllSharedSites
          sharedSiteIds
          templateCreditSessionTypes {
            siteSessionTypeId
            siteSessionTypeIds
            name
          }
        }
      }
    }
  }
`

export const availablePlanOptionsQuery = gql`
  query AvailablePlanOptions($siteId: ID!, $serviceId: ID!, $siteSessionTypeId: ID) {
    availablePlanOptions(
      siteId: $siteId
      serviceId: $serviceId
      siteSessionTypeId: $siteSessionTypeId
    ) {
      serviceTemplateId
      name
      price
      direction
      priceDelta
      tierOrder
    }
  }
`

export const calculateCartQuery = gql`
  query CalculateCart($siteId: String!, $items: [CalculateCartItemsInput!]!) {
    calculateCart(siteId: $siteId, items: $items) {
      items {
        proratedTotalAmount
        proratedDays
      }
      dueToday
    }
  }
`

export const serviceTemplateAgreementQuery = gql`
  query ServiceTemplateAgreement($serviceTemplateId: String!) {
    serviceTemplate(serviceTemplateId: $serviceTemplateId) {
      serviceTemplateId
      name
      type
      agreementEnabled
      agreementText
      siteAgreementTemplateId
      description
      price
      oneTimeSetupFee
      billingCycleAmount
      billingCycleUnit
      autoRenewEnabled
      toServiceTemplateId
      lengthAmount
      lengthUnit
      taxEnabled
      taxSettingIds
      isAllSharedSites
      sharedSiteIds
      cancellation {
        minimumTerm {
          charge {
            amount
          }
          after {
            amount
            unit
          }
        }
      }
      secondaryRecurringFee {
        enabled
        amount
        billingCycleAmount
        billingCycleUnit
        serviceFirstBillingDate {
          enabled
          onDate
          after {
            amount
            unit
          }
        }
      }
      credits {
        creditsType
        amount
        siteSessionTypeIds
        order
      }
    }
  }
`

export const previewMembershipPlanChangeQuery = gql`
  query PreviewMembershipPlanChange(
    $siteId: ID!
    $serviceId: ID!
    $toServiceTemplateId: ID!
    $customPrice: String
  ) {
    previewMembershipPlanChange(
      siteId: $siteId
      serviceId: $serviceId
      toServiceTemplateId: $toServiceTemplateId
      customPrice: $customPrice
    ) {
      proratedAmount
    }
  }
`

export const serviceCreditUsageHistoryQuery = gql`
  query ServiceCreditUsageHistory($serviceId: String!, $cursor: String, $pageSize: Float) {
    serviceCreditUsageHistory(serviceId: $serviceId, cursor: $cursor, pageSize: $pageSize) {
      pageInfo {
        hasNextPage
        totalRecords
        startCursor
        endCursor
      }
      results {
        cursor
        row {
          ledgerId
          bookingId
          siteId
          amount
          direction
          transactionType
          notes
          createdAt
          bookingStatus
          sessionName
          sessionDate
          sessionStartTime
          isBuddyBooking
        }
      }
    }
  }
`

export const serviceCreditSummaryPeriodsQuery = gql`
  query ServiceCreditSummaryPeriods(
    $serviceId: String!
    $siteSessionTypeId: String
    $siteSessionTypeIds: [String!]
  ) {
    serviceCreditSummaryPeriods(
      serviceId: $serviceId
      siteSessionTypeId: $siteSessionTypeId
      siteSessionTypeIds: $siteSessionTypeIds
    ) {
      totalPeriods
      periods {
        periodId
        periodNumber
        periodStartDate
        periodEndDate
        creditsAllocated
        creditsConsumed
        hasRefund
        isMaterialized
        isUnlimited
        isActivationPending
        activationExpiryDescription
      }
    }
  }
`

export const serviceCreditAdjustmentNotesQuery = gql`
  query ServiceCreditAdjustmentNotes($serviceId: String!, $cursor: String, $pageSize: Float) {
    serviceCreditAdjustmentNotes(serviceId: $serviceId, cursor: $cursor, pageSize: $pageSize) {
      hasMore
      endCursor
      notes {
        note
        timestamp
        authorId
        amount
        direction
      }
    }
  }
`
